import { useState, useEffect } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { createSection } from '../../graphql/mutation'
import { getAllStyles, getAllPromptsAdmin, getAllTags } from '../../graphql/queries'
import {
  Typography,
  Box,
  Button,
  MenuItem,
  FormControl,
  Select,
  TextField,
  Alert,
  CircularProgress,
  Snackbar,
  Autocomplete,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputLabel,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { useNavigate } from 'react-router-dom'
import { designType } from '../../utils/enum'



export default function CreateSectionPage() {
  const navigate = useNavigate()

  const [section, setSection] = useState({
    name: '',
    designType: '',
    rowNumber: '',
    isPublic: false,
    styleIds: [],
    promptIds: [],
    tagIds: [],
    highlighted: false,
  })

  const [selectedStyles, setSelectedStyles] = useState([])
  const [selectedPrompts, setSelectedPrompts] = useState([])
  const [selectedTags, setSelectedTags] = useState([])
  const [progress, setProgress] = useState(false)
  const [snackBarOpen, setSnackBarOpen] = useState(false)
  const [snackBarMessage, setSnackBarMessage] = useState(null)
  const [snackBarSeverity, setSnackBarSeverity] = useState('success')
  const [styleSearchTerm, setStyleSearchTerm] = useState('')
  const [promptSearchTerm, setPromptSearchTerm] = useState('')

  // Fetch styles and prompts for autocomplete
  const { data: stylesData, loading: stylesLoading } = useQuery(getAllStyles, {
    variables: { page: 1, pageSize: 1000 }
  })

  const { data: promptsData, loading: promptsLoading } = useQuery(getAllPromptsAdmin, {
    variables: { page: 1, pageSize: 1000 }
  })

  const { data: tagsData } = useQuery(getAllTags)

  const styles = stylesData?.getAllStylesAdmin?.styles || []
  const prompts = promptsData?.getAllPromptsAdmin?.prompts || []
  const tags = tagsData?.getAllTags || []

  // Filter styles and prompts based on search terms
  const filteredStyles = styles.filter(style =>
    style.name.toLowerCase().includes(styleSearchTerm.toLowerCase()) ||
    style.designType.toLowerCase().includes(styleSearchTerm.toLowerCase())
  ).sort((a, b) => {
    // Show selected items first
    const aSelected = selectedStyles.some(s => s.id === a.id)
    const bSelected = selectedStyles.some(s => s.id === b.id)
    if (aSelected && !bSelected) return -1
    if (!aSelected && bSelected) return 1
    return 0
  })

  const filteredPrompts = prompts.filter(prompt =>
    prompt.prompt.toLowerCase().includes(promptSearchTerm.toLowerCase()) ||
    prompt.Style?.name.toLowerCase().includes(promptSearchTerm.toLowerCase()) ||
    prompt.gender?.toLowerCase().includes(promptSearchTerm.toLowerCase()) ||
    prompt.toolType?.toLowerCase().includes(promptSearchTerm.toLowerCase())
  ).sort((a, b) => {
    // Show selected items first
    const aSelected = selectedPrompts.some(p => p.id === a.id)
    const bSelected = selectedPrompts.some(p => p.id === b.id)
    if (aSelected && !bSelected) return -1
    if (!aSelected && bSelected) return 1
    return 0
  })

  const handleChange = (e) => {
    const { name, value } = e.target
    setSection((prev) => ({ ...prev, [name]: value }))
  }

  const handleStylesChange = (event, newValue) => {
    setSelectedStyles(newValue)
    setSection(prev => ({
      ...prev,
      styleIds: newValue.map(style => style.id)
    }))
  }

  const handleStyleCheckboxChange = (style, checked) => {
    if (checked) {
      setSelectedStyles(prev => [...prev, style])
      setSection(prev => ({
        ...prev,
        styleIds: [...prev.styleIds, style.id]
      }))
    } else {
      setSelectedStyles(prev => prev.filter(s => s.id !== style.id))
      setSection(prev => ({
        ...prev,
        styleIds: prev.styleIds.filter(id => id !== style.id)
      }))
    }
  }

  const handlePromptsChange = (event, newValue) => {
    setSelectedPrompts(newValue)
    setSection(prev => ({
      ...prev,
      promptIds: newValue.map(prompt => prompt.id)
    }))
  }

  const handlePromptCheckboxChange = (prompt, checked) => {
    if (checked) {
      setSelectedPrompts(prev => [...prev, prompt])
      setSection(prev => ({
        ...prev,
        promptIds: [...prev.promptIds, prompt.id]
      }))
    } else {
      setSelectedPrompts(prev => prev.filter(p => p.id !== prompt.id))
      setSection(prev => ({
        ...prev,
        promptIds: prev.promptIds.filter(id => id !== prompt.id)
      }))
    }
  }

  const [createSectionMutation] = useMutation(createSection)

  const handleCreateSection = async () => {
    if (!section.name || !section.designType || !section.rowNumber) {
      setSnackBarMessage('Please fill in all required fields.')
      setSnackBarSeverity('error')
      setSnackBarOpen(true)
      return
    }

    setProgress(true)
    try {
      const response = await createSectionMutation({
        variables: {
          name: section.name,
          designType: section.designType,
          rowNumber: Number(section.rowNumber),
          isPublic: section.isPublic,
          styleIds: section.styleIds,
          promptIds: section.promptIds,
          tagIds: section.tagIds,
          highlighted: section.highlighted,
        },
      })

      setProgress(false)
      if (response.data) {
        setSnackBarMessage('Section created successfully')
        setSnackBarOpen(true)
        setSnackBarSeverity('success')
        setTimeout(() => {
          navigate('/sections')
        }, 1000)
      } else {
        setSnackBarMessage('Create failed')
        setSnackBarOpen(true)
        setSnackBarSeverity('error')
      }
    } catch (error) {
      console.error('Error creating section:', error)
      setSnackBarMessage(`Create failed: ${error.message}`)
      setSnackBarSeverity('error')
      setSnackBarOpen(true)
    } finally {
      setProgress(false)
    }
  }

  const handleSnackBarClose = () => {
    setSnackBarOpen(false)
  }

  return (
    <Box sx={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      <Snackbar
        open={snackBarOpen}
        autoHideDuration={3000}
        onClose={handleSnackBarClose}
        message={snackBarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert
          onClose={handleSnackBarClose}
          severity={snackBarSeverity}
          sx={{ width: '100%' }}
        >
          {snackBarMessage}
        </Alert>
      </Snackbar>

      {/* Sticky Header with Actions */}
      <Box 
        sx={{ 
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          backgroundColor: 'white',
          borderBottom: '1px solid #e9ecef',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 4, flexWrap: 'wrap', gap: 2 }}>
          <Typography variant='h4' sx={{ color: '#212529', fontWeight: 'bold' }}>
            Create Section
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleCreateSection}
            sx={{ 
              borderRadius: 2,
              px: 4,
              backgroundColor: '#3498db',
              '&:hover': { backgroundColor: '#2980b9' }
            }}
          >
            Save Section
          </Button>
        </Box>
      </Box>
      
      {progress && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(255,255,255,0.7)',
            zIndex: 1300,
          }}
        >
          <CircularProgress size={80} thickness={5} />
        </Box>
      )}

      {/* Main Content Container */}
      <Box sx={{ maxWidth: '1400px', mx: 'auto', p: 4, display: 'flex', flexDirection: 'column', gap: 4 }}>
        
        {/* Basic Information Card */}
        <Paper sx={{ borderRadius: 3, boxShadow: '0 4px 16px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
          <Box sx={{ p: 4, pb: 2 }}>
            <Typography variant='h5' sx={{ fontWeight: 'bold', color: '#2c3e50' }}>
              Basic Information
            </Typography>
          </Box>

          {/* Basic Fields Accordion */}
          <Accordion defaultExpanded sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}>
            <AccordionSummary 
              expandIcon={<ExpandMoreIcon />}
              sx={{ 
                px: 4, 
                backgroundColor: '#f8f9fa',
                '&:hover': { backgroundColor: '#e9ecef' }
              }}
            >
              <Typography variant='h6' sx={{ fontWeight: 600, color: '#495057' }}>
                Section Details
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 4, pb: 3 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {[
                    { name: 'name', label: 'Section Name', type: 'text' },
                    { name: 'rowNumber', label: 'Row Number', type: 'number' },
                  ].map(({ name, label, type }) => (
                    <TextField
                      fullWidth
                      key={name}
                      id={`${name}-input`}
                      label={label}
                      variant="outlined"
                      name={name}
                      type={type}
                      onChange={handleChange}
                      inputProps={{ ...(type === 'number' && { min: 0 }) }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  ))}
                </Box>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {/* Design Type */}
                  <FormControl fullWidth>
                    <InputLabel>Design Type</InputLabel>
                    <Select 
                      name="designType" 
                      onChange={handleChange} 
                      value={section.designType}
                      label="Design Type"
                      variant="outlined"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    >
                      {designType.map((i) => (
                        <MenuItem key={i.value} value={i.value}>
                          {i.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  {/* Tags Selection */}
                  <Autocomplete
                    multiple
                    options={tags}
                    getOptionLabel={(option) => option.name}
                    value={selectedTags}
                    onChange={(event, newValue) => {
                      setSelectedTags(newValue)
                      setSection(prev => ({
                        ...prev,
                        tagIds: newValue.map(tag => tag.id)
                      }))
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        label="Tags"
                        placeholder="Select tags..."
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option.id}
                          label={option.name}
                          size="small"
                          sx={{
                            backgroundColor: '#e3f2fd',
                            color: '#1565c0',
                            '&:hover': { backgroundColor: '#bbdefb' }
                          }}
                        />
                      ))
                    }
                  />
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Visibility Settings Accordion */}
{ /*         <Accordion defaultExpanded sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}>
            <AccordionSummary 
              expandIcon={<ExpandMoreIcon />}
              sx={{ 
                px: 4, 
                backgroundColor: '#f8f9fa',
                '&:hover': { backgroundColor: '#e9ecef' }
              }}
            >
              <Typography variant='h6' sx={{ fontWeight: 600, color: '#495057' }}>
                Visibility Settings
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 4, pb: 3 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                {[
                  { key: 'isPublic', label: 'Is Public', checked: section.isPublic },
                  { key: 'highlighted', label: 'Highlighted', checked: section.highlighted }
                ].map(({ key, label, checked }) => (
                  <Box key={key} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                    <Typography sx={{ fontWeight: 500, color: '#2c3e50' }}>{label}</Typography>
                    <Box
                      onClick={() => setSection(prev => ({ ...prev, [key]: !prev[key] }))}
                      sx={{
                        width: 50,
                        height: 24,
                        borderRadius: 12,
                        backgroundColor: checked ? '#4caf50' : '#ddd',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '2px',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          backgroundColor: 'white',
                          transform: checked ? 'translateX(26px)' : 'translateX(0)',
                          transition: 'transform 0.3s ease',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                        }}
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
            </AccordionDetails>
          </Accordion> */}
        </Paper>

        {/* Selected Items Display */}
        {(selectedStyles.length > 0 || selectedPrompts.length > 0) && (
          <Paper sx={{ p: 4, borderRadius: 3, boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }}>
            <Typography variant='h5' sx={{ mb: 3, fontWeight: 'bold', color: '#2c3e50' }}>
              Selected Items
            </Typography>
            {selectedStyles.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#495057' }}>
                  Selected Styles ({selectedStyles.length}):
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {selectedStyles.map((style) => (
                    <Chip
                      key={style.id}
                      label={style.name}
                      onDelete={() => handleStyleCheckboxChange(style, false)}
                      variant="outlined"
                      size="small"
                      sx={{
                        backgroundColor: '#e8f5e8',
                        color: '#2e7d32',
                        borderColor: '#81c784',
                        '&:hover': { backgroundColor: '#c8e6c9' }
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}
            
            {selectedPrompts.length > 0 && (
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#495057' }}>
                  Selected Prompts ({selectedPrompts.length}):
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {selectedPrompts.map((prompt) => (
                    <Chip
                      key={prompt.id}
                      label={prompt.prompt.length > 40 ? prompt.prompt.substring(0, 40) + '...' : prompt.prompt}
                      onDelete={() => handlePromptCheckboxChange(prompt, false)}
                      variant="outlined"
                      size="small"
                      sx={{
                        backgroundColor: '#fff3e0',
                        color: '#e65100',
                        borderColor: '#ffb74d',
                        '&:hover': { backgroundColor: '#ffe0b2' }
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Paper>
        )}

        {/* Selection Tables Card */}
        <Paper sx={{ p: 4, borderRadius: 3, boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }}>
          <Typography variant='h5' sx={{ mb: 4, fontWeight: 'bold', color: '#2c3e50' }}>
            Select Items
          </Typography>
          
          {/* Tables Container with Flex Layout */}
          <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
            {/* Styles Table */}
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                Select Styles
              </Typography>
              
              {/* Styles Search Bar */}
              <TextField
                fullWidth
                size="small"
                placeholder="Search styles by name or design type..."
                value={styleSearchTerm}
                onChange={(e) => setStyleSearchTerm(e.target.value)}
                sx={{ mb: 2 }}
              />
              
              <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">Select</TableCell>
                      <TableCell>Banner</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Type</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stylesLoading ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <CircularProgress size={20} />
                        </TableCell>
                      </TableRow>
                    ) : filteredStyles.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Typography variant="body2" color="text.secondary">
                            No styles found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredStyles.map((style) => {
                        const isSelected = selectedStyles.some(s => s.id === style.id)
                        return (
                          <TableRow key={style.id} hover>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={isSelected}
                                onChange={(e) => handleStyleCheckboxChange(style, e.target.checked)}
                              />
                            </TableCell>
                            <TableCell>
                              {style.banner ? (
                                <img
                                  src={style.banner}
                                  alt="Style banner"
                                  style={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: '4px',
                                    objectFit: 'cover',
                                  }}
                                />
                              ) : (
                                <Box
                                  sx={{
                                    width: 40,
                                    height: 40,
                                    backgroundColor: '#f5f5f5',
                                    borderRadius: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <Typography variant="caption" color="text.secondary">
                                    No Image
                                  </Typography>
                                </Box>
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {style.name}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {style.designType}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            {/* Prompts Table */}
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                Select Prompts
              </Typography>
              
              {/* Prompts Search Bar */}
              <TextField
                fullWidth
                size="small"
                placeholder="Search prompts by text, style, gender, or tool type..."
                value={promptSearchTerm}
                onChange={(e) => setPromptSearchTerm(e.target.value)}
                sx={{ mb: 2 }}
              />
              
              <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">Select</TableCell>
                      <TableCell>Banner</TableCell>
                      <TableCell>Prompt</TableCell>
                      <TableCell>Style</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {promptsLoading ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <CircularProgress size={20} />
                        </TableCell>
                      </TableRow>
                    ) : filteredPrompts.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Typography variant="body2" color="text.secondary">
                            No prompts found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredPrompts.map((prompt) => {
                        const isSelected = selectedPrompts.some(p => p.id === prompt.id)
                        return (
                          <TableRow key={prompt.id} hover>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={isSelected}
                                onChange={(e) => handlePromptCheckboxChange(prompt, e.target.checked)}
                              />
                            </TableCell>
                            <TableCell>
                              {prompt.banner ? (
                                <img
                                  src={prompt.banner}
                                  alt="Prompt banner"
                                  style={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: '4px',
                                    objectFit: 'cover',
                                  }}
                                />
                              ) : (
                                <Box
                                  sx={{
                                    width: 40,
                                    height: 40,
                                    backgroundColor: '#f5f5f5',
                                    borderRadius: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <Typography variant="caption" color="text.secondary">
                                    No Image
                                  </Typography>
                                </Box>
                              )}
                            </TableCell>
                            <TableCell sx={{ maxWidth: 200 }}>
                              <Typography variant="body2">
                                {prompt.prompt.length > 60 ? prompt.prompt.substring(0, 60) + '...' : prompt.prompt}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {prompt.Style?.name || 'No Style'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Box>
  )
} 